import { Field, FieldProps, FieldValidator, useFormikContext } from "formik";
import React, { ReactElement } from "react";
import {
    FormFeedback,
    FormGroup,
    FormText,
    InputGroup,
    InputGroupAddon,
} from "reactstrap";
import { object } from "yup";

import { debounce as debounceFn } from "@utils/general";
import { ConditionalWrapper } from "@utils/helpers";

import { MicroCopy } from "@components/Form/FormFields/MicroCopy/microCopy.component";
import { Tooltip } from "@components/General/Tooltip/tooltip.component";

import { FieldLabel } from "./fieldLabel.component";

export type BaseProps = {
    name: string;
    label: string;
    microCopy?: string;
    required?: boolean;
    hideLabel?: boolean;
    hideMicroCopy?: boolean;
    optionalIndication?: boolean;
    validationRule?: any;
    debounce?: boolean;
    addon?: () => JSX.Element;
    tooltip?: { title: string; body: string };
    isCheckbox?: boolean;
};

type Props = BaseProps & {
    children: (props: ChildProps) => ReactElement;
};

export type ChildProps = FieldProps & {};

const debounceValidation = debounceFn(
    (
        resolve: (value: string | void | PromiseLike<string | void>) => void,
        validateFieldFn: FieldValidator,
    ) => {
        return validateFieldFn(resolve);
    },
    300,
);

const BaseField = ({
    label,
    name,
    microCopy,
    required,
    hideLabel,
    hideMicroCopy = false,
    optionalIndication = true,
    validationRule,
    isCheckbox = false,
    children,
    debounce,
    addon,
    tooltip,
}: Props) => {
    const formik = useFormikContext<any>();

    const validateField: FieldValidator = value => {
        if (!validationRule) {
            return;
        }

        // Use this so the path can be passed
        const schema = object().shape({
            [name]: validationRule.label(label.toLowerCase()),
        });

        const validate = (
            resolve: (
                value: string | void | PromiseLike<string | void>,
            ) => void,
        ) => {
            schema
                .validate(
                    { ...formik.values, [name]: value },
                    { context: formik, abortEarly: true },
                )
                .then(() => {
                    // The field is valid
                    resolve();
                })
                .catch(err => {
                    // The field is invalid and has errors
                    resolve(err.errors);
                });
        };

        return new Promise(resolve => {
            if (debounce) {
                debounceValidation(resolve, validate);
            } else {
                validate(resolve);
            }
        });
    };

    return (
        <ConditionalWrapper
            condition={!!addon}
            wrapper={children => <InputGroup>{children}</InputGroup>}
            alternative={children => <FormGroup>{children}</FormGroup>}
        >
            {!isCheckbox && (
                <FieldLabel
                    isCheckBox={isCheckbox}
                    name={name}
                    hideLabel={hideLabel}
                    label={label}
                    optionalIndication={optionalIndication}
                    required={required}
                />
            )}
            {tooltip && !isCheckbox && (
                <Tooltip title={tooltip?.title} innerHTML={tooltip?.body} />
            )}
            {!hideMicroCopy && microCopy && (
                <FormText
                    color="default"
                    dangerouslySetInnerHTML={{ __html: microCopy }}
                />
            )}
            {!hideMicroCopy && !microCopy && <MicroCopy fieldId={name} />}
            <Field name={name} validate={validateField}>
                {({ field, meta, form }: ChildProps) => {
                    return (
                        <>
                            {children({
                                meta,
                                field,
                                form,
                            })}
                            {addon && (
                                <InputGroupAddon addonType="append">
                                    {addon()}
                                </InputGroupAddon>
                            )}
                            {meta.touched && !!meta.error && (
                                <FormFeedback>{meta.error}</FormFeedback>
                            )}
                        </>
                    );
                }}
            </Field>
            {isCheckbox && (
                <FieldLabel
                    isCheckBox={isCheckbox}
                    name={name}
                    hideLabel={hideLabel}
                    label={label}
                    optionalIndication={optionalIndication}
                    required={required}
                />
            )}
            {tooltip && isCheckbox && (
                <Tooltip title={tooltip?.title} innerHTML={tooltip?.body} />
            )}
        </ConditionalWrapper>
    );
};

export { BaseField };
